import { useEffect, useRef, useState } from "react";
import { addDoc, collection, onSnapshot, serverTimestamp } from "firebase/firestore";
import { X } from "lucide-react";
import { db } from "../firebase";
import AssistanceTasks from "./AssistanceTasks";
import "./AssistanceClients.css";

const emptyClient = { firstName: "", lastName: "", personalNumber: "", address: "", phone: "" };
const fields = [
  { name: "firstName", label: "Förnamn", autoComplete: "given-name" },
  { name: "lastName", label: "Efternamn", autoComplete: "family-name" },
  { name: "personalNumber", label: "Personnummer", placeholder: "ÅÅÅÅMMDD-XXXX", pattern: "(?:[0-9]{6}|[0-9]{8})[-+]?[0-9]{4}", title: "Ange 10 eller 12 siffror, med eller utan bindestreck." },
  { name: "address", label: "Adress", autoComplete: "street-address" },
  { name: "phone", label: "Telefonnummer", type: "tel", autoComplete: "tel" },
];

export default function AssistanceClients({ companyUid }) {
  const [form, setForm] = useState(emptyClient);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(Boolean(companyUid));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [message, setMessage] = useState("");
  const [search, setSearch] = useState("");
  const [creating, setCreating] = useState(false);
  const [tasksClient, setTasksClient] = useState(null);
  const savingRef = useRef(false);
  const startCreating = () => { setForm(emptyClient); setError(""); setMessage(""); setCreating(true); };
  const closeCreating = () => { if (!savingRef.current) { setCreating(false); setError(""); } };

  useEffect(() => {
    if (!companyUid) return;
    return onSnapshot(collection(db, "users", companyUid, "clients"), (snapshot) => {
      setClients(snapshot.docs.map((client) => ({ ...client.data(), id: client.id }))
        .sort((a, b) => `${a.lastName} ${a.firstName}`.localeCompare(`${b.lastName} ${b.firstName}`, "sv")));
      setLoading(false);
      setLoadError("");
    }, () => {
      setLoading(false);
      setLoadError("Kunde inte hämta klienterna. Försök ladda om sidan.");
    });
  }, [companyUid]);

  const saveClient = async (event) => {
    event.preventDefault();
    if (!companyUid || savingRef.current) return;
    const client = Object.fromEntries(Object.entries(form).map(([key, value]) => [key, value.trim()]));
    setError("");
    setMessage("");
    if (Object.values(client).some((value) => !value)) {
      setError("Fyll i alla klientuppgifter.");
      return;
    }
    savingRef.current = true;
    setSaving(true);
    try {
      await addDoc(collection(db, "users", companyUid, "clients"), { ...client, createdAt: serverTimestamp() });
      setForm(emptyClient);
      setMessage("Klienten har sparats.");
      setCreating(false);
    } catch {
      setError("Kunde inte spara klienten. Försök igen.");
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  const matchingClients = clients.filter((client) => fields.map(({ name }) => client[name] || "").join(" ").toLocaleLowerCase("sv").includes(search.trim().toLocaleLowerCase("sv")));

  return <section className="client-overview assistance-clients" aria-label="Mina klienter">
    {tasksClient && <div className="smart-assistance__tasks-overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setTasksClient(null); }} onKeyDown={(event) => { if (event.key === "Escape") setTasksClient(null); }}><section className="smart-assistance__tasks-dialog" role="dialog" aria-modal="true" aria-label={`Uppgifter för ${tasksClient.firstName} ${tasksClient.lastName}`}><div className="smart-assistance__tasks-dialog-header"><h2>Uppgifter för {tasksClient.firstName} {tasksClient.lastName}</h2><button type="button" aria-label="Stäng" onClick={() => setTasksClient(null)}>Stäng</button></div><AssistanceTasks key={tasksClient.id} companyUid={companyUid} clientId={tasksClient.id} clientName={`${tasksClient.firstName} ${tasksClient.lastName}`} /></section></div>}
    {!companyUid && <p role="status">Välj ett företag för att skapa och visa klienter.</p>}
    <div className="client-overview__filters">
      <label className="client-overview__search"><span className="client-overview__visually-hidden">Sök klient</span><input type="search" aria-label="Sök klient" placeholder="Sök på klient..." value={search} onChange={(event) => setSearch(event.target.value)} /></label>
    </div>
    {loadError && <p role="alert">{loadError}</p>}
    <div className="client-overview__summary">
      <div className="assistance-clients__summary-heading"><h1>Mina klienter ({matchingClients.length})</h1><button className="assistance-clients__create-button" type="button" disabled={!companyUid} onClick={startCreating}>Skapa ny klient</button></div>
      <div><span><strong>Totalt antal klienter:</strong> {clients.length}</span></div>
    </div>
    {loading && companyUid && <p role="status">Hämtar klienter…</p>}
    {!loading && !loadError && companyUid && <div className="client-overview__table smart-assistance__client-list"><table>
      <caption className="smart-assistance__visually-hidden">Sparade klientuppgifter</caption>
      <thead><tr>{fields.map(({ name, label }) => <th scope="col" key={name}>{label.toLocaleUpperCase("sv")}</th>)}<th scope="col">UPPGIFTER</th></tr></thead>
      <tbody>{matchingClients.map((client) => <tr key={client.id}>{fields.map(({ name }) => <td key={name}>{client[name]}</td>)}<td><button type="button" className="smart-assistance__client-tasks-button" onClick={() => setTasksClient(client)}>Skapa uppgifter</button></td></tr>)}
        {!matchingClients.length && <tr><td colSpan={fields.length + 1}>{clients.length ? "Inga klienter matchar din sökning." : "Inga klienter har lagts till ännu."}</td></tr>}
      </tbody>
    </table></div>}
    {message && <p role="status">{message}</p>}
    {creating && <div className="assistance-clients__overlay" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) closeCreating(); }} onKeyDown={(event) => { if (event.key === "Escape") closeCreating(); }}>
      <section className="assistance-clients__dialog" role="dialog" aria-modal="true" aria-label="Skapa klient">
        <div className="assistance-clients__dialog-heading"><h2>Skapa klient</h2><button type="button" aria-label="Stäng" disabled={saving} onClick={closeCreating}><X size={22} /></button></div>
        <form className="smart-assistance__client-form" onSubmit={saveClient}>
          <fieldset disabled={!companyUid || saving}>
            <div className="smart-assistance__client-fields">
              {fields.map(({ name, label, ...attributes }) => <label key={name}>
                {label}
                <input {...attributes} name={name} required maxLength={name === "address" ? 500 : 100}
                  value={form[name]} onChange={(event) => { setForm({ ...form, [name]: event.target.value }); setMessage(""); }} />
              </label>)}
            </div>
            {error && <p role="alert">{error}</p>}
            <button type="submit">{saving ? "Sparar…" : "Spara klient"}</button>
          </fieldset>
        </form>
      </section>
    </div>}
  </section>;
}
